/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/common/ContinuousScrollDatePicker.tsx
 * PURPOSE: Reusable "chọn 1 ngày / khoảng ngày" filter control — implements
 *          Luật 13 (UI-UX-DECISION-REGISTER.md §2): CẤP 1 hiển thị các tháng
 *          dạng DÒNG-TUẦN CHẢY LIÊN TỤC (cuộn dọc, không giới hạn 2 phía),
 *          KHÔNG dùng `‹ ›` paging thay-hẳn-nội-dung. Bấm 1 ô = chọn ngày đơn
 *          (xác nhận bằng click-outside/Tab); bấm thêm 1 ô khác = chuyển sang
 *          chọn khoảng (xác nhận tường minh bằng nút "Xem N ngày").
 *          First consumer: SC-011-ATTEND-MGR `<thời gian>` [3]. Luật 13's own
 *          follow-up note says the NEXT screen needing a date/range filter
 *          should reuse this component instead of rebuilding the old `‹ ›`
 *          pattern — hence a standalone component, not inlined in the page.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @changelog
 * - 2026-09-14 (@coder): Bug thật (2 ảnh, popover "Chọn ca" field "Từ ngày –
 *   đến ngày" dùng DateRangePicker.tsx đã có ring "hôm nay" nhưng lịch [CAL]
 *   toolbar dùng component NÀY thì KHÔNG) — component này thiếu hẳn chỉ báo
 *   "hôm nay" dù wireframe §[CAL] đã đặc tả từ đầu ("ring viền coffee-500
 *   1.5px, không tô đặc"). Thêm `isToday`/`.dayToday`, độc lập hoàn toàn với
 *   `isViewMarker`/`.dayMarker` (đánh dấu GIÁ TRỊ ĐANG ÁP DỤNG, không phải
 *   ngày-hệ-thống-thật) và với trạng thái đã-chọn (`.dayPicked`/`.dayInRange`)
 *   — một ô có thể vừa là hôm nay vừa đang được chọn, cả hai tín hiệu cùng
 *   hiện (xem comment tại `.dayToday` trong .module.css lý do dùng box-shadow
 *   outset thay vì border).
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — filter chọn ngày/khoảng ngày
 * - Impact: UX Critical — mọi màn cần lọc theo ngày (chấm công, phân ca báo cáo…)
 * - Stakeholders: Branch Managers, Shift Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (reusable UI component)
 * - Pattern: Controlled component (value/onApply) + portal panel (position:fixed,
 *   giống PortalMenu.tsx) — panel không lồng trong container overflow nào của
 *   trang gọi nó (Luật 12 tinh thần, dù đây không phải tooltip).
 * - Dependencies: react-dom (createPortal)
 *
 * @cross-references
 * - Luật 13: docs/02-design/04-ui-ux-design/UI-UX-DECISION-REGISTER.md §2 mục 13
 * - UI/behavior SSOT: docs/02-design/04-ui-ux-design/persona-flows/
 *   WIREFRAME-V0-MASTER-REGISTRY.md § SC-011-ATTEND-MGR khối "DATE FILTER"
 * - First consumer: frontend/src/pages/manager/ManagerAttendancePage.tsx
 * - Sibling pattern: frontend/src/components/common/PortalMenu.tsx (portal + fixed)
 * ===============================================================================
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ContinuousScrollDatePicker.module.css";

export type DateFilterValue =
  | { mode: "single"; date: string }
  | { mode: "range"; from: string; to: string };

interface Props {
  value: DateFilterValue;
  onApply: (value: DateFilterValue) => void;
  maxRangeDays?: number;
  triggerClassName?: string;
  triggerLabel?: string;
  ariaLabel?: string;
}

const ROW_HEIGHT = 38;
const PANEL_HEIGHT = 340;
const EXTEND_MONTHS = 6;
const EDGE_ROWS_TRIGGER = 4;
const WEEKDAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromISO = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};
export const formatDateVi = (iso: string) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
const formatDayMonth = (iso: string) => {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

const monthIndexOf = (year: number, month0: number) => year * 12 + month0;
const yearOfIdx = (idx: number) => Math.floor(idx / 12);
const monthOfIdx = (idx: number) => ((idx % 12) + 12) % 12;

const mondayOnOrBefore = (d: Date) => {
  const dow = d.getDay(); // 0=Sun..6=Sat
  const back = dow === 0 ? 6 : dow - 1;
  const r = new Date(d);
  r.setDate(r.getDate() - back);
  return r;
};
const sundayOnOrAfter = (d: Date) => {
  const dow = d.getDay();
  const fwd = dow === 0 ? 0 : 7 - dow;
  const r = new Date(d);
  r.setDate(r.getDate() + fwd);
  return r;
};

interface DayCell {
  date: Date;
  iso: string;
  dimmed: boolean;
}

/** Sinh danh sách hàng-tuần (7 ô/hàng) LIÊN TỤC từ đầu startMonthIdx tới cuối
 * endMonthIdx — ô mờ CHỈ ở phần đệm trước ngày-1-tháng-đầu / sau ngày-cuối-
 * tháng-cuối (đúng Luật 13: "chỉ ở hàng đầu/cuối của TOÀN vùng đã nạp"). */
function buildRows(startMonthIdx: number, endMonthIdx: number): DayCell[][] {
  const firstOfStart = new Date(yearOfIdx(startMonthIdx), monthOfIdx(startMonthIdx), 1);
  const lastOfEnd = new Date(yearOfIdx(endMonthIdx), monthOfIdx(endMonthIdx) + 1, 0);
  const gridStart = mondayOnOrBefore(firstOfStart);
  const gridEnd = sundayOnOrAfter(lastOfEnd);
  const days: DayCell[] = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    const d = new Date(cursor);
    days.push({ date: d, iso: toISO(d), dimmed: d < firstOfStart || d > lastOfEnd });
    cursor.setDate(cursor.getDate() + 1);
  }
  const rows: DayCell[][] = [];
  for (let i = 0; i < days.length; i += 7) rows.push(days.slice(i, i + 7));
  return rows;
}

/** Tháng "chiếm đa số" trong 1 hàng-tuần (dùng cho nhãn sticky) — hoà thì lấy
 * tháng của ô thứ 4 (Thứ 5, ô giữa hàng) làm tie-break. */
function dominantMonth(row: DayCell[]): { year: number; month0: number } {
  const counts = new Map<string, { year: number; month0: number; n: number }>();
  row.forEach((cell) => {
    const key = `${cell.date.getFullYear()}-${cell.date.getMonth()}`;
    const entry = counts.get(key);
    if (entry) entry.n += 1;
    else counts.set(key, { year: cell.date.getFullYear(), month0: cell.date.getMonth(), n: 1 });
  });
  let best: { year: number; month0: number; n: number } | null = null;
  counts.forEach((v) => {
    if (!best || v.n > best.n) best = v;
  });
  if (best && (best as { n: number }).n >= 4) return best;
  const mid = row[3].date;
  return { year: mid.getFullYear(), month0: mid.getMonth() };
}

const daysBetweenInclusive = (fromIso: string, toIso: string) =>
  Math.round((fromISO(toIso).getTime() - fromISO(fromIso).getTime()) / 86_400_000) + 1;

const ChevronDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);
const ChevronUpIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={14} height={14} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
  </svg>
);
const ChevronLeftIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={16} height={16} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);
const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={18} height={18} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export const ContinuousScrollDatePicker: React.FC<Props> = ({
  value,
  onApply,
  maxRangeDays = 92,
  triggerClassName,
  triggerLabel,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const anchorDateIso = value.mode === "single" ? value.date : value.from;
  const anchorDate = fromISO(anchorDateIso);

  const [level, setLevel] = useState<1 | 2 | 3>(1);
  const [startMonthIdx, setStartMonthIdx] = useState(() => monthIndexOf(anchorDate.getFullYear(), anchorDate.getMonth()) - EXTEND_MONTHS);
  const [endMonthIdx, setEndMonthIdx] = useState(() => monthIndexOf(anchorDate.getFullYear(), anchorDate.getMonth()) + EXTEND_MONTHS);
  const [gridYear, setGridYear] = useState(anchorDate.getFullYear());
  const [decadeStart, setDecadeStart] = useState(() => Math.floor(anchorDate.getFullYear() / 10) * 10);

  const [draftFrom, setDraftFrom] = useState<string | null>(null);
  const [draftTo, setDraftTo] = useState<string | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [stickyLabel, setStickyLabel] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const pendingScrollAdjust = useRef<number | null>(null);
  // Bug thật 2026-09-07 (đối chiếu với `sc011-build-prototype.html`, cờ
  // `CAL.userScrolled` tương đương): gán `scrollTop` bằng code (useLayoutEffect
  // neo lịch khi vừa mở, dòng dưới) CŨNG bắn sự kiện `scroll` — nếu không có cờ
  // này, `handleScroll` tính nhãn tháng theo hàng đang cuộn tới NGAY LẬP TỨC,
  // trước khi người dùng chạm vào bất cứ đâu, và hàng đó (rowIdx-1, cố tình hé
  // lộ vài dòng cuối tháng trước) có thể thuộc tháng TRƯỚC → nhãn sai ngay lúc
  // vừa mở. Chỉ đổi nhãn sau 1 thao tác cuộn/▲▼ THẬT.
  const userScrolledRef = useRef(false);

  const rows = useMemo(() => buildRows(startMonthIdx, endMonthIdx), [startMonthIdx, endMonthIdx]);

  const resetToAnchor = () => {
    const d = fromISO(anchorDateIso);
    setLevel(1);
    setStartMonthIdx(monthIndexOf(d.getFullYear(), d.getMonth()) - EXTEND_MONTHS);
    setEndMonthIdx(monthIndexOf(d.getFullYear(), d.getMonth()) + EXTEND_MONTHS);
    setGridYear(d.getFullYear());
    setDecadeStart(Math.floor(d.getFullYear() / 10) * 10);
    setDraftFrom(null);
    setDraftTo(null);
    setRangeError(null);
  };

  const openPanel = () => {
    if (triggerRef.current) setAnchorRect(triggerRef.current.getBoundingClientRect());
    resetToAnchor();
    userScrolledRef.current = false;
    setOpen(true);
  };

  const closePanel = (apply: boolean) => {
    if (apply && draftFrom && !draftTo) {
      onApply({ mode: "single", date: draftFrom });
    }
    // Khoảng ngày (draftFrom && draftTo) KHÔNG có xác nhận-ngầm-qua-click-outside —
    // đóng ở đây luôn HUỶ, không áp — đúng Luật 13 §"CHỌN KHOẢNG".
    setOpen(false);
  };

  // 2026-09-07 (@coder, sync wireframe theo chỉ đạo "sync đúng theo wireframe
  // trước, sau đó prototype"): bản đích WIREFRAME-V0-MASTER-REGISTRY.md §[CAL]
  // — "lịch cuộn tới hôm nay và CĂN GIỮA khung nhìn" — áp dụng CHUNG cho MỌI
  // lịch cuộn dọc liên tục trong app (Luật 13), không riêng màn nào. Trước đây
  // component NÀY lại neo ĐẦU khung (rowIdx-1, chỉ hé lộ 1 hàng phía trên) —
  // lệch với chính quy tắc app tự đặt ra, không có lý do UX nào đủ mạnh để
  // giữ khác biệt (ngược lại: neo đầu khung chỉ cho thấy NGỮ CẢNH TƯƠNG LAI,
  // neo giữa cho thấy CẢ quá khứ gần lẫn tương lai — hữu ích khi người dùng
  // cần liếc lại vài ngày/tuần trước trong lúc chọn khoảng bắt đầu từ/gần
  // hôm nay). Quyết định: ĐỔI sang căn giữa, tính theo chiều cao THẬT của
  // vùng cuộn (`clientHeight`, không hardcode số hàng) để đúng ở mọi kích
  // thước panel/breakpoint thực tế.
  useLayoutEffect(() => {
    if (!open || level !== 1 || !containerRef.current) return;
    const anchorMonthIdx = monthIndexOf(anchorDate.getFullYear(), anchorDate.getMonth());
    const rowIdx = rows.findIndex((r) => r.some((c) => monthIndexOf(c.date.getFullYear(), c.date.getMonth()) === anchorMonthIdx && !c.dimmed));
    if (rowIdx >= 0) {
      const el = containerRef.current;
      // Fallback 3 hàng khi clientHeight chưa đo được (vd panel chưa layout
      // xong/môi trường test không có kích thước thật) — vẫn ưu tiên căn
      // giữa thay vì quay lại neo đầu khung.
      const visibleRows = el.clientHeight > 0 ? el.clientHeight / ROW_HEIGHT : 3;
      el.scrollTop = Math.max(0, (rowIdx - visibleRows / 2) * ROW_HEIGHT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, level]);

  // Giữ đúng vị trí nhìn thấy sau khi prepend thêm tháng ở đầu vùng cuộn.
  useLayoutEffect(() => {
    if (pendingScrollAdjust.current != null && containerRef.current) {
      containerRef.current.scrollTop += pendingScrollAdjust.current;
      pendingScrollAdjust.current = null;
    }
  }, [rows]);

  const rafRef = useRef<number | null>(null);
  const handleScroll = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const el = containerRef.current;
      if (!el) return;
      const topRowIdx = Math.min(rows.length - 1, Math.max(0, Math.round(el.scrollTop / ROW_HEIGHT)));
      if (userScrolledRef.current) {
        const dom = dominantMonth(rows[topRowIdx]);
        setStickyLabel(`Tháng ${dom.month0 + 1}, ${dom.year}`);
      }

      if (el.scrollTop < EDGE_ROWS_TRIGGER * ROW_HEIGHT) {
        const oldRowCount = rows.length;
        setStartMonthIdx((prev) => prev - EXTEND_MONTHS);
        pendingScrollAdjust.current = null; // set below once new rows computed via effect on rows length delta
        // Đo delta hàng qua rebuild ngay (đồng bộ) để bù scrollTop chính xác.
        const newRows = buildRows(startMonthIdx - EXTEND_MONTHS, endMonthIdx);
        pendingScrollAdjust.current = (newRows.length - oldRowCount) * ROW_HEIGHT;
      } else if (el.scrollTop + el.clientHeight > el.scrollHeight - EDGE_ROWS_TRIGGER * ROW_HEIGHT) {
        setEndMonthIdx((prev) => prev + EXTEND_MONTHS);
      }
    });
  };

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      closePanel(true);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftFrom, draftTo]);

  const scrollToMonth = (targetYear: number, targetMonth0: number) => {
    const el = containerRef.current;
    if (!el) return;
    const idx = monthIndexOf(targetYear, targetMonth0);
    if (idx < startMonthIdx + 1) {
      setStartMonthIdx(idx - EXTEND_MONTHS);
    }
    if (idx > endMonthIdx - 1) {
      setEndMonthIdx(idx + EXTEND_MONTHS);
    }
    requestAnimationFrame(() => {
      const r = buildRows(Math.min(startMonthIdx, idx - EXTEND_MONTHS), Math.max(endMonthIdx, idx + EXTEND_MONTHS));
      const rowIdx = r.findIndex((row) => row.some((c) => monthIndexOf(c.date.getFullYear(), c.date.getMonth()) === idx && !c.dimmed));
      if (rowIdx >= 0 && el) el.scrollTo({ top: Math.max(0, (rowIdx - 1) * ROW_HEIGHT), behavior: "smooth" });
    });
  };

  const nudgeMonth = (dir: 1 | -1) => {
    const el = containerRef.current;
    if (!el) return;
    userScrolledRef.current = true;
    const topRowIdx = Math.round(el.scrollTop / ROW_HEIGHT);
    const dom = dominantMonth(rows[Math.min(rows.length - 1, Math.max(0, topRowIdx))]);
    const targetIdx = monthIndexOf(dom.year, dom.month0) + dir;
    scrollToMonth(yearOfIdx(targetIdx), monthOfIdx(targetIdx));
  };

  const pickDay = (iso: string) => {
    if (!draftFrom) {
      setDraftFrom(iso);
      setDraftTo(null);
      setRangeError(null);
      return;
    }
    if (draftTo) {
      // 3rd click while a range is already assembled — start a fresh pick
      // (wireframe doesn't specify this case explicitly; treating it as a
      // new selection is the least-surprising behaviour).
      setDraftFrom(iso);
      setDraftTo(null);
      setRangeError(null);
      return;
    }
    if (iso <= draftFrom) {
      setDraftFrom(iso);
      setDraftTo(null);
      setRangeError(null);
      return;
    }
    const span = daysBetweenInclusive(draftFrom, iso);
    if (span > maxRangeDays) {
      setRangeError(`Khoảng ngày tối đa ${maxRangeDays} ngày.`);
      return;
    }
    setDraftTo(iso);
    setRangeError(null);
  };

  const confirmRange = () => {
    if (!draftFrom || !draftTo) return;
    onApply({ mode: "range", from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const cancelRange = () => {
    setDraftFrom(null);
    setDraftTo(null);
    setRangeError(null);
  };

  const closeMobile = () => {
    // ✕ full-screen mobile: lúc chỉ có 1 ô tô đậm → xác nhận NGAY (tương
    // đương bấm-ra-ngoài desktop); chưa bấm ô nào / đang giữa bước chọn
    // khoảng → huỷ.
    closePanel(true);
  };

  const label =
    triggerLabel ??
    (value.mode === "single" ? formatDateVi(value.date) : `${formatDateVi(value.from)} – ${formatDateVi(value.to)}`);

  const isViewMarker = (iso: string): boolean =>
    !draftFrom && value.mode === "single" && value.date === iso;
  const isDraftSingle = (iso: string) => draftFrom === iso && !draftTo;
  const isDraftRangeEnd = (iso: string) => (draftFrom === iso || draftTo === iso) && !!draftTo;
  const isDraftRangeMid = (iso: string) => !!draftFrom && !!draftTo && iso > draftFrom && iso < draftTo;
  // [CAL] "Dấu 'hôm nay'" (WIREFRAME-V0-MASTER-REGISTRY.md §[CAL]) — so bằng
  // ngày HỆ THỐNG THẬT (`new Date()`), KHÔNG phải `anchorDate`/tháng đang
  // cuộn tới. Cố tình KHÔNG memo hoá: chi phí tạo 1 Date/format ISO mỗi
  // render là không đáng kể, và việc này giữ đúng "hôm nay" nếu panel vô
  // tình mở vắt qua nửa đêm — memo theo `[]`/`open` sẽ đông cứng sai.
  const todayIso = toISO(new Date());
  const isToday = (iso: string): boolean => iso === todayIso;

  const panel = open && anchorRect && (
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ top: anchorRect.bottom + 6, left: Math.max(8, anchorRect.left) }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? "Chọn ngày hoặc khoảng ngày"}
    >
      <button type="button" className={styles.mobileClose} aria-label="Đóng lịch" onClick={closeMobile}>
        <CloseIcon />
      </button>

      {level === 1 && (
        <>
          <div className={styles.head}>
            <button type="button" className={styles.monthLabel} onClick={() => setLevel(2)}>
              {stickyLabel || `Tháng ${anchorDate.getMonth() + 1}, ${anchorDate.getFullYear()}`}
            </button>
            <div className={styles.headNav}>
              <button type="button" className={styles.navBtn} aria-label="Tháng trước" onClick={() => nudgeMonth(-1)}>
                <ChevronUpIcon />
              </button>
              <button type="button" className={styles.navBtn} aria-label="Tháng sau" onClick={() => nudgeMonth(1)}>
                <ChevronDownIcon />
              </button>
            </div>
          </div>
          <div className={styles.weekdayRow}>
            {WEEKDAY_LABELS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
          <div
            ref={containerRef}
            className={styles.scrollArea}
            style={{ height: PANEL_HEIGHT }}
            onScroll={handleScroll}
            onWheel={() => { userScrolledRef.current = true; }}
            onPointerDown={() => { userScrolledRef.current = true; }}
            onTouchStart={() => { userScrolledRef.current = true; }}
            onKeyDown={() => { userScrolledRef.current = true; }}
          >
            {rows.map((row, i) => (
              <div className={styles.weekRow} key={i} style={{ height: ROW_HEIGHT }}>
                {row.map((cell) => {
                  const marker = isViewMarker(cell.iso);
                  const single = isDraftSingle(cell.iso);
                  const rangeEnd = isDraftRangeEnd(cell.iso);
                  const rangeMid = isDraftRangeMid(cell.iso);
                  const today = isToday(cell.iso);
                  return (
                    <button
                      type="button"
                      key={cell.iso}
                      className={[
                        styles.dayCell,
                        cell.dimmed ? styles.dayDimmed : "",
                        marker ? styles.dayMarker : "",
                        single || rangeEnd ? styles.dayPicked : "",
                        rangeMid ? styles.dayInRange : "",
                        // Độc lập với marker/picked/inRange ở trên — cả hai
                        // tín hiệu (hôm nay + đã-chọn) phải cùng hiện được
                        // trên một ô, không cái nào loại trừ cái kia.
                        today ? styles.dayToday : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => !cell.dimmed && pickDay(cell.iso)}
                      disabled={cell.dimmed}
                      aria-pressed={single || rangeEnd}
                    >
                      {cell.date.getDate()}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {draftFrom && draftTo && (
            <div className={styles.rangeFooter}>
              <div className={styles.rangeFooterText}>
                Từ {formatDayMonth(draftFrom)} · Đến {formatDayMonth(draftTo)}
              </div>
              <div className={styles.rangeFooterActions}>
                <button type="button" className={styles.btnGhost} onClick={cancelRange}>
                  Huỷ
                </button>
                <button type="button" className={styles.btnPrimarySm} onClick={confirmRange}>
                  Xem {daysBetweenInclusive(draftFrom, draftTo)} ngày
                </button>
              </div>
            </div>
          )}
          {rangeError && (
            <div className={styles.rangeError} role="alert">
              {rangeError}
            </div>
          )}
        </>
      )}

      {level === 2 && (
        <div className={styles.gridLevel}>
          <div className={styles.head}>
            <button type="button" className={styles.monthLabel} onClick={() => setLevel(3)}>
              {gridYear}
            </button>
            <div className={styles.headNav}>
              <button type="button" className={styles.navBtn} aria-label="Năm trước" onClick={() => setGridYear((y) => y - 1)}>
                <ChevronLeftIcon />
              </button>
              <button type="button" className={styles.navBtn} aria-label="Năm sau" onClick={() => setGridYear((y) => y + 1)}>
                <ChevronRightIcon />
              </button>
            </div>
          </div>
          <div className={styles.monthGrid}>
            {Array.from({ length: 12 }).map((_, m) => (
              <button
                type="button"
                key={m}
                className={styles.gridCell}
                onClick={() => {
                  setLevel(1);
                  scrollToMonth(gridYear, m);
                }}
              >
                Th {m + 1}
              </button>
            ))}
          </div>
        </div>
      )}

      {level === 3 && (
        <div className={styles.gridLevel}>
          <div className={styles.head}>
            <span className={styles.monthLabel}>
              {decadeStart} – {decadeStart + 9}
            </span>
            <div className={styles.headNav}>
              <button type="button" className={styles.navBtn} aria-label="Thập kỷ trước" onClick={() => setDecadeStart((y) => y - 10)}>
                <ChevronLeftIcon />
              </button>
              <button type="button" className={styles.navBtn} aria-label="Thập kỷ sau" onClick={() => setDecadeStart((y) => y + 10)}>
                <ChevronRightIcon />
              </button>
            </div>
          </div>
          <div className={styles.monthGrid}>
            {Array.from({ length: 10 }).map((_, i) => {
              const y = decadeStart + i;
              return (
                <button
                  type="button"
                  key={y}
                  className={styles.gridCell}
                  onClick={() => {
                    setGridYear(y);
                    setLevel(2);
                  }}
                >
                  {y}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClassName}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          if (open) closePanel(true);
          else openPanel();
        }}
      >
        {label} <ChevronDownIcon />
      </button>
      {open && createPortal(panel, document.body)}
    </>
  );
};

export default ContinuousScrollDatePicker;
